import { HighchartsReactRefObject } from 'highcharts-react-official';
import { useCallback, useEffect, useRef } from 'react';

export function useRedraw(
  chartComponentRef: React.RefObject<HighchartsReactRefObject>,
  chartContainer: HTMLDivElement | null
) {
  const element = chartComponentRef.current;
  const container = chartContainer;
  const isRotated = useRef(false);
  // console.log('Use redraw', chartComponentRef, chartContainer);
  const callback = useCallback(
    (source: ResizeObserverEntry[] | Event) => {
      if (!container || !element?.chart || !element?.container?.current) {
        return;
      }

      // Handle ResizeObserverEntry[] - actual resize events
      if (Array.isArray(source)) {
        // ResizeObserver triggered by container size change
        // Let Highcharts handle the reflow automatically
        element.chart.reflow();
        return;
      }

      // Handle custom Event objects (rotation, orientation)
      if (source instanceof Event && source.type === 'rotatechart') {
        const rect = container.getBoundingClientRect();
        const width = !isRotated.current
          ? rect.height
          : element.container.current.dataset.width || rect.height;
        const height = !isRotated.current
          ? rect.width
          : element.container.current.dataset.height || rect.width;
        element.chart.setSize(Number(width), Number(height), false);
        // Remember the original dimensions in the element
        element.container.current.dataset.width = height.toString();
        element.container.current.dataset.height = width.toString();
        isRotated.current = !isRotated.current;
        setTimeout(() => {
          element.chart.reflow();
        }, 0);
      }
    },
    [element, container]
  );
  useEffect(() => {
    // Add resize observer for element if provided
    const element = chartComponentRef.current;
    if (!element) {
      return;
    }
    const resize = () => {
      element?.chart?.reflow();
    };
    const observer = element ? new ResizeObserver(callback) : null;
    observer &&
      element &&
      element.container.current &&
      observer.observe(element.container.current);
    window.addEventListener('resize', resize);
    window.addEventListener('rotatechart', callback);
    window.addEventListener('orientationchange', callback);
    return () => {
      window.removeEventListener('resize', resize);
      window.removeEventListener('rotatechart', callback);
      window.removeEventListener('orientationchange', callback);
      observer && observer.disconnect();
    };
  }, [callback, chartComponentRef]);
}
